import * as React from "react";
import { Suspense } from "react";

import { AsyncComponent4 } from "@impero-com/common/components/asyncComponent";
import {
    withRouter,
    WithRouterProps,
    withParams,
} from "@impero-com/common/components/routerUtils";
import { LoggedInHTTPError } from "@impero-com/common/errorPages/loggedInHTTPError";
import { AjaxManager } from "@impero-com/common/utils/ajax";
import * as Cmp from "@impero-com/common/utils/comparison";
import * as DV from "@impero-com/common/utils/dataView";
import { entries } from "@impero-com/common/utils/entries";
import { t } from "@impero-com/common/utils/i18n";
import { SimpleAndFilter } from "@impero-com/common/utils/indexing";
import { assertNever, set } from "@impero-com/common/utils/obj";
import { OrgCookie } from "@impero-com/common/utils/orgCookie";
import { PageSpinner } from "@impero-com/ui";
import { Spin } from "antd";
import axios, { AxiosResponse, CancelTokenSource } from "axios";
import { isEqual, keyBy } from "lodash-es";
import memoizeOne from "memoize-one";
import moment, { Moment } from "moment-timezone";
import { Params, useLocation } from "react-router-dom";

import { getAllControls } from "api/generated/controls/controls";
import { getEntities } from "api/generated/entities/entities";
import type { UserProfile } from "api/generated/model";
import {
    Level,
    ControlOverviewListDto,
    RiskDirectoryNodeResources,
    ControlProgramOverview,
    EntityOverviewListDto,
    EntityView,
    GetEntitiesParams,
    GetRisksRiskDirectoryIdNodesParams,
} from "api/generated/model";
import * as RN from "api/generated/model";
import {
    deleteRisksRiskDirectoryId,
    getRisksRiskDirectoryId,
    getRisksRiskDirectoryIdNodes,
    patchRisksRiskDirectoryIdNodesNodeId,
    postRisksRiskDirectoryIdNodes,
} from "api/generated/risks/risks";
import * as R from "org/risks/types";
import { UpdateRootNodeLinkedResourcesCallback } from "org/risks/widgets/attachedFilesField";
import { HashSwitch } from "org/router";
import { Guid } from "utils/guid";
import { useFeatureFlag } from "utils/hooks/useFeatureFlag";

import { RISK_MODULE } from "../url";

import { DirectoryPage } from "./directory";
import { buildResponseEnumMap } from "./impactLikelihood";
import { MapPageInsightsWrapper, MapPageFnWrapper } from "./map";
import { MapInsights } from "./map/MapInsights";
import * as T from "./types";
import {
    forwardToRisk,
    getDirectoryChildren,
    getDirectoryPath,
    getDirectoryPathFragment,
    updateDirectorySubTree,
    getNodeGuid,
    getSubsequentIds,
    getId,
    getNodeLinkedEntities,
    normalizeColumn,
    unwrapForwardToRisk,
    getScope,
    groupingToRegularMetadataColumnId,
} from "./utils";

export const RISK_DIRECTORY_HASH = "#risk_directory";
export const RISK_DIRECTORY_DATE_SEARCH_PARAM = "at";
export const RISK_DIRECTORY_FROM_DATE_SEARCH_PARAM = "from";
export const RISK_DIRECTORY_TO_DATE_SEARCH_PARAM = "to";
export const RISK_DIRECTORY_ENTITY_ID_SEARCH_PARAM = "entity_id";
export const RISK_DIRECTORY_FILTER_SEARCH_PARAM = "filter";
export const RISK_MAP_HASH = "#risk_map";
export const RISK_MAP_INSIGHTS_HASH = "#risk_map_insights";
// TODO: Delete this URL once the new insights tab have been validated.
export const RISK_MAP_INSIGHTS_HASH_OLD = "#risk_map_insights_old";
export const RISK_MAP_HISTORY_HASH = "#risk_map_history";

class DirectoryWithRouter extends React.PureComponent<DirectoryProps> {
    computedNodeProps = memoizeOne(computeNodeProps);
    metadataColumnMap = memoizeOne(computeMetadataColumnMap);
    visibleMetadataColumns = memoizeOne(computeVisibleMetadataColumns);
    entities = memoizeOne(computeEntities);
    metadataColumns = memoizeOne((riskDirectory: R.RiskDirectoryView) =>
        this.buildMetadataColumns(riskDirectory)
    );
    responseEnumMap = memoizeOne(buildResponseEnumMap);
    controlIdToControlAndActions = memoizeOne(
        computeControlIdToControlAndActions
    );

    render = () => {
        const {
            mode,
            deleteDirectory,
            updateNode,
            reloadDirectory,
            onDeleteNode,
            createNode,
            lastAddedNodeId,
            navigate,
            location,
            ajaxManager,
            orgCookie,
            orgProfileCookie,
            riskDirectoryView,
            rootNode,
            entityList,
            allControls,
            focusedEntity,
            updateRootNodeLinkedResources,
        } = this.props;

        switch (mode.type) {
            case "Directory": {
                return (
                    <DirectoryPage
                        common={this.commonGetters()}
                        responseEnumMap={this.responseEnumMap(
                            this.props.riskDirectoryView
                        )}
                        deleteDirectory={deleteDirectory}
                        updateNode={updateNode}
                        onDeleteNode={onDeleteNode}
                        createNode={createNode}
                        lastAddedNodeId={lastAddedNodeId}
                        riskDirectoryView={riskDirectoryView}
                        rootNode={rootNode}
                        entityList={entityList}
                        allControls={allControls}
                        ajaxManager={ajaxManager}
                        orgCookie={orgCookie}
                        orgProfileCookie={orgProfileCookie}
                        updateRootNodeLinkedResources={
                            updateRootNodeLinkedResources
                        }
                    />
                );
            }
            case "Map": {
                const filter = mode.filter;
                return (
                    <MapPageFnWrapper
                        common={this.commonGetters()}
                        responseEnumMap={this.responseEnumMap(
                            this.props.riskDirectoryView
                        )}
                        focusedEntity={focusedEntity}
                        mode={mode}
                        updateNode={updateNode}
                        reloadDirectory={reloadDirectory}
                        entityList={entityList}
                        allControls={allControls}
                        riskDirectoryView={riskDirectoryView}
                        rootNode={rootNode}
                        filter={filter}
                        navigate={navigate}
                        location={location}
                        ajaxManager={ajaxManager}
                        orgCookie={orgCookie}
                        orgProfileCookie={orgProfileCookie}
                        updateRootNodeLinkedResources={
                            updateRootNodeLinkedResources
                        }
                    />
                );
            }
            case "InsightsMap": {
                const filter = mode.filter;
                return (
                    <MapPageInsightsWrapper
                        common={this.commonGetters()}
                        focusedEntity={focusedEntity}
                        responseEnumMap={this.responseEnumMap(
                            this.props.riskDirectoryView
                        )}
                        mode={mode}
                        updateNode={updateNode}
                        reloadDirectory={reloadDirectory}
                        entityList={entityList}
                        allControls={allControls}
                        riskDirectoryView={riskDirectoryView}
                        rootNode={rootNode}
                        filter={filter}
                        ajaxManager={ajaxManager}
                        orgCookie={orgCookie}
                        orgProfileCookie={orgProfileCookie}
                        updateRootNodeLinkedResources={
                            updateRootNodeLinkedResources
                        }
                    />
                );
            }
            case "HistoricalMap": {
                const filter = mode.filter;
                return (
                    <MapPageFnWrapper
                        common={this.commonGetters()}
                        focusedEntity={focusedEntity}
                        responseEnumMap={this.responseEnumMap(
                            this.props.riskDirectoryView
                        )}
                        mode={mode}
                        updateNode={updateNode}
                        reloadDirectory={reloadDirectory}
                        entityList={entityList}
                        allControls={allControls}
                        riskDirectoryView={riskDirectoryView}
                        rootNode={rootNode}
                        filter={filter}
                        navigate={navigate}
                        location={location}
                        ajaxManager={ajaxManager}
                        orgCookie={orgCookie}
                        orgProfileCookie={orgProfileCookie}
                        updateRootNodeLinkedResources={
                            updateRootNodeLinkedResources
                        }
                    />
                );
            }
            default: {
                return assertNever(mode);
            }
        }
    };

    private commonGetters = (): T.CommonGetters => {
        const {
            computedNodeProps,
            metadataColumnMap,
            entities,
            metadataColumns,
            controlIdToControlAndActions,
            getDescription,
            getRisk,
            getProcesses,
            getComputedNodeEntityIds,
            getComputedEntityIds,
            getDirectoryAncestorsEntitiesWithNodePaths,
            getDirectoryDescendantsEntitiesWithNodePaths,
            getEntities,
            renderEntities,
        } = this;

        return {
            computedNodeProps,
            metadataColumnMap,
            entities,
            metadataColumns,
            controlIdToControlAndActions,
            getDescription,
            getRisk,
            getProcesses,
            getComputedNodeEntityIds,
            getComputedEntityIds,
            getDirectoryAncestorsEntitiesWithNodePaths,
            getDirectoryDescendantsEntitiesWithNodePaths,
            getEntities,
            renderEntities,
        };
    };

    private buildMetadataColumns = (
        riskDirectory: R.RiskDirectoryView
    ): Record<
        Level,
        (T.MetadataColumn & { id: T.MetadataColumnExtractorId })[]
    > => {
        const { riskDirectoryLevel, entityLevel } =
            this.metadataColumnMap(riskDirectory);
        const visibleMetadataColumns =
            this.visibleMetadataColumns(riskDirectory);

        const computeMC = (
            metadataColumnMap: T.MetadataColumnMap<T.MetadataColumnExtractorId>
        ) =>
            entries(metadataColumnMap)
                .map(([id, col]) => ({ id, ...col }))
                .filter(
                    (col) => "Process" in col.fields || "Risk" in col.fields
                )
                .filter((col) =>
                    Object.values(col.fields).some(
                        (f) => f !== undefined && f.fieldType.type !== "Heading"
                    )
                )
                .filter((col) =>
                    Object.values(col.fields).some(
                        (field) =>
                            field !== undefined &&
                            visibleMetadataColumns.has(field.id)
                    )
                )
                .sort(
                    Cmp.map(
                        (col) => col.position,
                        Cmp.undefinedLast(Cmp.primitive)
                    )
                );

        return {
            riskDirectoryLevel: computeMC(riskDirectoryLevel),
            entityLevel: computeMC(entityLevel),
        };
    };

    private getDescription = unwrapForwardToRisk((node) =>
        node.type === "RiskDirectory" ? "" : node.content.description
    );
    private getRisk = forwardToRisk((node) =>
        node.type === "Risk" ? node.content.name : undefined
    );
    private getProcesses = (desc: T.AnyDescriptor) => {
        // In order to perform an AND search we need to return all processes for each node, including the current process node.
        switch (desc.value.type) {
            case "RiskDirectory":
                return [];
            case "Process":
                // XXX: This is based on the assumption than the first node and only the first node in ancestors is a risk directory node.
                return [
                    ...(desc.ancestors.slice(1) as R.ProcessNode[]),
                    desc.value,
                ];
            case "Risk":
            case "ExpandedRiskNode":
                // XXX: This is based on the assumption than the first node and only the first node in ancestors is a risk directory node.
                return desc.ancestors.slice(1) as R.ProcessNode[];
            default:
                assertNever(desc.value);
        }
    };
    private getComputedNodeEntityIds = (nodeId: Guid): number[] => {
        const { rootNode } = this.props;
        const computedNodeProps = this.computedNodeProps(
            rootNode.content.resource.rootNode
        );
        const { entityIds } = computedNodeProps[nodeId];
        return entityIds;
    };
    private getComputedEntityIds = (nodeId: Guid): number[] => {
        const { rootNode } = this.props;
        const computedNodeProps = this.computedNodeProps(
            rootNode.content.resource.rootNode
        );
        const entities: number[] = [];
        let nextId: Guid | undefined = nodeId;
        while (nextId) {
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
            const { parentId, entityIds } = computedNodeProps[nextId] as {
                parentId?: Guid;
                entityIds: number[];
            };
            entities.push(...entityIds);
            nextId = parentId;
        }
        return entities;
    };
    private getDirectoryAncestorsEntitiesWithNodePaths = (
        ancestors: T.DirectoryNode[]
    ): Record<number, string> => {
        const { rootNode } = this.props;
        const computedNodeProps = this.computedNodeProps(
            rootNode.content.resource.rootNode
        );
        const entitiesWithNodePaths: Record<number, string> = {};
        let path = "";
        ancestors.forEach((node, nodeIndex) => {
            if (nodeIndex > 0) {
                path += "\xA0/\xA0";
            }
            path += getDirectoryPathFragment(node);
            const { entityIds } = computedNodeProps[node.id];
            for (const entityId of entityIds) {
                const paths = entitiesWithNodePaths[entityId];
                if (paths) {
                    throw new Error("Entity link duplicate in ancestors");
                }
                entitiesWithNodePaths[entityId] = path;
            }
        });
        return entitiesWithNodePaths;
    };
    private getDirectoryDescendantsEntitiesWithNodePaths = (
        desc: DV.NodeDescriptor<T.DirectoryNode>
    ): Record<number, string[]> => {
        if (getScope(desc.value).scope === "Local") {
            return {};
        }
        const { rootNode } = this.props;
        const computedNodeProps = this.computedNodeProps(
            rootNode.content.resource.rootNode
        );
        const dataSource: DV.DataSource<DV.NodeDescriptor<T.DirectoryNode>> =
            new DV.MultiTreeDataSource<T.DirectoryNode>(
                getDirectoryChildren(desc.value),
                getDirectoryChildren,
                getId,
                (node) => node.content.name
            );
        const entitiesWithNodePaths: Record<number, string[]> = {};
        let descPath = getDirectoryPath(desc);
        if (desc.ancestors.length > 0) {
            descPath += "\xA0/\xA0";
        }
        descPath += getDirectoryPathFragment(desc.value);
        DV.forEach(dataSource.iter(), (node) => {
            const { entityIds } = computedNodeProps[node.value.id];
            for (const entityId of entityIds) {
                let path = descPath;
                if (node.ancestors.length > 0) {
                    path += "\xA0/\xA0";
                    path += getDirectoryPath(node);
                }
                path += "\xA0/\xA0";
                path += getDirectoryPathFragment(node.value);
                const paths = entitiesWithNodePaths[entityId];
                if (paths) {
                    paths.push(path);
                } else {
                    entitiesWithNodePaths[entityId] = [path];
                }
            }
        });
        return entitiesWithNodePaths;
    };
    private getEntities = (desc: T.AnyDescriptor): number[] => {
        switch (desc.value.type) {
            case "ExpandedRiskNode":
                return [desc.value.entityId];
            case "RiskDirectory":
                return this.getComputedEntityIds(desc.value.id);
            case "Risk":
            case "Process": {
                const nodeContent = desc.value.content;
                switch (nodeContent.scope.type) {
                    case "Global":
                    case "GlobalWithContent":
                        return this.getComputedEntityIds(desc.value.id);
                    case "Local":
                    case "LocalWithContent":
                        return [nodeContent.scope.entityId];
                    default:
                        return assertNever(nodeContent.scope);
                }
            }
            default:
                assertNever(desc.value);
        }
    };

    private renderEntities = (node: T.AnyDescriptor) => {
        const { entityList, rootNode } = this.props;
        const entities = this.entities(entityList, rootNode);
        const entityIds = this.getEntities(node);
        if (entityIds.length > 5) {
            return t("RiskDirectory_View_EntityCount", {
                count: entityIds.length,
            });
        } else {
            return entityIds
                .map((entityId) => entities[entityId].name)
                .join(", ");
        }
    };
}

const Directory = withRouter(DirectoryWithRouter);

function computeNodeProps(rootNode: R.RiskDirectoryNode): T.NodeComputedProps {
    function processNode(
        node: T.DirectoryNode,
        map: T.NodeComputedProps,
        parent?: T.DirectoryNode
    ) {
        map[node.id] = {
            parentId: parent && parent.id,
            entityIds: getNodeLinkedEntities(node),
        };
        if (node.type !== "Risk") {
            node.nodes.forEach((child) => processNode(child, map, node));
        }
    }

    const map: T.NodeComputedProps = {};
    processNode(rootNode, map);
    return map;
}

function computeMetadataColumnMap(
    view: R.RiskDirectoryView
): Record<Level, T.MetadataColumnMap<T.MetadataColumnExtractorId>> {
    interface ComputePropsContext {
        map: T.MetadataColumnMap<T.MetadataColumnGroupingId>;
        processCount: number;
    }

    const computeProps = (
        f: R.MetadataField,
        nodeType: R.NodeType,
        context: ComputePropsContext,
        level: Level
    ) => {
        if (f.fieldType.type === "Heading") {
            return;
        }
        const { map } = context;
        const name = normalizeColumn(f, level);
        if (name in map) {
            map[name].fields[nodeType] = f;
        } else {
            map[name] = {
                name: f.name,
                fields: {
                    [nodeType]: f,
                },
            };
        }
        if (nodeType === "Process") {
            context.processCount += 1;
        }
    };

    const riskDirectoryProps: ComputePropsContext = {
        map: {},
        processCount: 0,
    };

    const {
        content: {
            resource: { structure },
        },
    } = view;

    Object.entries(structure.nodeTypeToDirectoryLevelFields).forEach(
        ([nodeType, fields]) => {
            fields.forEach((field) =>
                computeProps(
                    field,
                    nodeType as R.NodeType,
                    riskDirectoryProps,
                    "riskDirectoryLevel"
                )
            );
        }
    );

    const entityProps: ComputePropsContext = {
        map: {},
        processCount: 0,
    };
    structure.entityLevelFields.forEach((f) =>
        computeProps(f, "Risk", entityProps, "entityLevel")
    );

    interface SetPositionContext {
        map: T.MetadataColumnMap<T.MetadataColumnGroupingId>;
        processPosition: number;
        riskPosition: number;
    }

    const setPosition = (id: Guid, context: SetPositionContext) => {
        Object.values(context.map).forEach((props) => {
            Object.entries(props.fields).forEach(([nodeType, field]) => {
                if (field === undefined) {
                    return;
                } // Should never happen, but I didn't manage to enforce it in types
                if (id === field.id) {
                    const positionWithOffset =
                        nodeType === "Process"
                            ? context.processPosition++
                            : context.riskPosition++;
                    props.position =
                        props.position === undefined
                            ? positionWithOffset
                            : Math.min(props.position, positionWithOffset);
                }
            });
        });
    };

    const riskDirectoryPos: SetPositionContext = {
        map: riskDirectoryProps.map,
        processPosition: 0,
        riskPosition: riskDirectoryProps.processCount,
    };

    const entityPos: SetPositionContext = {
        map: entityProps.map,
        processPosition: 0,
        riskPosition: entityProps.processCount,
    };

    for (const section of structure.sections) {
        for (const column of section.sectionColumns) {
            for (const fieldId of column) {
                setPosition(fieldId, riskDirectoryPos);
                setPosition(fieldId, entityPos);
            }
        }
    }

    function postProcessIds(
        map: T.MetadataColumnMap<T.MetadataColumnGroupingId>
    ): T.MetadataColumnMap<T.MetadataColumnExtractorId> {
        const newMap: T.MetadataColumnMap<T.MetadataColumnExtractorId> = {};
        for (const [id, column] of entries(map)) {
            const newId = groupingToRegularMetadataColumnId(
                id,
                column.fields.Process?.id,
                column.fields.Risk?.id
            );
            newMap[newId] = column;
        }
        return newMap;
    }

    return {
        riskDirectoryLevel: postProcessIds(riskDirectoryPos.map),
        entityLevel: postProcessIds(entityPos.map),
    };
}

function computeVisibleMetadataColumns(view: R.RiskDirectoryView): Set<Guid> {
    const set = new Set<Guid>();
    view.content.resource.structure.sections.forEach((section) =>
        section.sectionColumns.forEach((column) =>
            column.forEach((metadataGuid) => set.add(metadataGuid))
        )
    );
    return set;
}

function computeEntities(
    response: EntityOverviewListDto,
    rootNode: RN.RiskDirectoryNodeWithDetailsDto
): Record<number, EntityView> {
    const fallbackEntities = keyBy(
        response.content.map(({ resource }) => ({
            ...resource,
            description: resource.description ?? null,
            responsibleId: resource.responsibleId ?? null,
        })),
        (entity) => entity.id
    );
    const entities = keyBy(
        rootNode.linkedResources.entities,
        (entity) => entity.id
    );
    return {
        ...fallbackEntities,
        ...entities,
    };
}

function computeControlIdToControlAndActions(
    rootNode: RN.RiskDirectoryNodeWithDetailsDto,
    allControls: ControlOverviewListDto
): Record<Guid, T.ControlAndActions> {
    function controlProgram(controlProgramId: number): ControlProgramOverview {
        let controlProgram: ControlProgramOverview | undefined =
            allControls.linkedResources.find(
                (cp) => cp.resource.id === controlProgramId
            )?.resource;
        if (controlProgram === undefined) {
            controlProgram = rootNode.linkedResources.controlPrograms.find(
                (cp) => cp.resource.id === controlProgramId
            )?.resource;
            if (controlProgram === undefined) {
                throw new Error(
                    `Cannot find control program with id ${controlProgramId}`
                );
            }
        }
        return controlProgram;
    }

    function controlProgramAction(controlProgramId: number): T.ActionType {
        const actions = allControls.linkedResources.find(
            (cp) => cp.resource.id === controlProgramId
        )!.actions;
        // Checking accessRight and entities is superfluous since it implies content
        if (
            actions.edit.content ||
            actions.edit.accessRights ||
            actions.edit.entities
        ) {
            return T.Action.EDIT;
        } else if (actions.view) {
            return T.Action.VIEW;
        } else {
            return null;
        }
    }

    const map: Record<Guid, T.ControlAndActions> = allControls.content.reduce(
        (acc: Record<Guid, T.ControlAndActions>, dto) => {
            const control = dto.resource;
            const actions = dto.actions;
            let controlAction: T.ActionType;
            if (actions.edit) {
                controlAction = T.Action.EDIT;
            } else if (actions.view) {
                controlAction = T.Action.VIEW;
            } else {
                controlAction = null;
            }

            acc[control.id] = {
                control,
                controlAction,
                controlProgram: controlProgram(control.controlProgramId),
                controlProgramAction: controlProgramAction(
                    control.controlProgramId
                ),
            };

            return acc;
        },
        {}
    );

    rootNode.linkedResources.controls.forEach((control) => {
        if (!(control.id in map)) {
            map[control.id] = {
                control,
                controlProgram: controlProgram(control.controlProgramId),
                controlAction: null,
                controlProgramAction: null,
            };
        }
    });

    return map;
}

type DirectoryMode =
    | {
          type: "Directory";
          savedDate: Moment | null;
      }
    | {
          type: "Map";
          savedDate: Moment | null;
          filter: SimpleAndFilter | null;
      }
    | {
          type: "HistoricalMap";
          targetDate: Moment | null;
          filter: SimpleAndFilter | null;
      }
    | {
          type: "InsightsMap";
          fromDate: Moment | null;
          toDate: Moment | null;
          filter: SimpleAndFilter | null;
      };

interface DirectoryProps extends T.CommonProps, WithRouterProps {
    mode: DirectoryMode;
    deleteDirectory: () => void;
    updateNode: <NODE extends T.AnyNode>(
        action: T.NodeUpdateAction<NODE>,
        targetNode: NODE
    ) => Promise<void>;
    reloadDirectory: () => void;
    onDeleteNode: (node: T.DirectoryNode) => void;
    createNode: (newNode: R.NewRiskDirectoryNode) => Promise<void>;
    lastAddedNodeId?: Guid;
    focusedEntity: T.FocusedEntity;
    updateRootNodeLinkedResources: UpdateRootNodeLinkedResourcesCallback;
}

class TimestampCachedPromise<T> {
    cached: Record<string, AxiosResponse<T>> = {};

    async get(
        params: { at?: string | null },
        request: () => Promise<AxiosResponse<T>>
    ): Promise<AxiosResponse<T>> {
        const timestamp = params.at;
        if (timestamp === undefined || timestamp === null) {
            return request();
        }

        const cached = this.cached[timestamp];
        if (cached === undefined) {
            const response = await request();
            this.cached[timestamp] = response;
            return response;
        }
        return Promise.resolve(cached);
    }
}

class DirectoryLoader extends React.PureComponent<
    DirectoryLoaderProps,
    DirectoryLoaderState
> {
    asyncComponent =
        React.createRef<
            AsyncComponent4<
                R.RiskDirectoryView,
                T.RiskDirectoryNodeDtoWithChanges,
                EntityOverviewListDto,
                ControlOverviewListDto
            >
        >();
    cancelTokenSource: CancelTokenSource[] = [];
    cachedPromiseRiskDirView =
        new TimestampCachedPromise<R.RiskDirectoryView>();
    cachedPromiseRiskDirNode =
        new TimestampCachedPromise<T.RiskDirectoryNodeDtoWithChanges>();
    cachedPromiseEntities = new TimestampCachedPromise<EntityOverviewListDto>();
    focusedEntity = (): T.FocusedEntity => {
        const focusedEntityId = this.props.params.entityId;
        if (focusedEntityId) {
            const focusedEntityIdNumber = Number(focusedEntityId);
            if (Number.isNaN(focusedEntityIdNumber)) {
                console.error(
                    "provided entity_id search param is invalid, expected number"
                );
            }
            return {
                type: "FOCUSED",
                entityId: focusedEntityIdNumber,
            };
        } else {
            return {
                type: "NONE",
            };
        }
    };

    // When building the component, we want to inject the targeted timestamp (if any)
    constructor(props: DirectoryLoaderProps) {
        super(props);

        this.state = {
            status: { type: "DEFAULT" as const },
            lastAddedNodeId: undefined,
        };
    }

    componentWillUnmount() {
        this.cancelAllUpdate();
    }

    render = () => {
        const {
            mode,
            ajaxManager,
            orgCookie,
            orgProfileCookie,
            controlEntitiesEnabled,
            params,
        } = this.props;
        const { id, entityId } = params;
        const { lastAddedNodeId } = this.state;

        let at: string; // "live" | ISO timestamp
        let focus: string; // "none" | entityId
        switch (mode.type) {
            case "Directory": {
                at = "live";
                focus = "none";
                break;
            }
            case "Map": {
                at = "live";
                if (entityId) {
                    focus = entityId;
                } else {
                    focus = "none";
                }
                break;
            }
            case "HistoricalMap": {
                if (mode.targetDate) {
                    at = mode.targetDate.toISOString(true);
                } else {
                    at = "live";
                }
                if (entityId) {
                    focus = entityId;
                } else {
                    focus = "none";
                }
                break;
            }
            case "InsightsMap": {
                if (mode.toDate) {
                    at = mode.toDate.toISOString(true);
                } else {
                    at = "live";
                }
                if (entityId) {
                    focus = entityId;
                } else {
                    focus = "none";
                }
                break;
            }
            default: {
                return assertNever(mode);
            }
        }

        const searchParamsStructure = { at: at === "live" ? undefined : at };
        const searchParamsNodes: GetRisksRiskDirectoryIdNodesParams = {
            ...searchParamsStructure,
            entity_id: focus === "none" ? undefined : Number(focus),
        };
        const searchParamsEntities: GetEntitiesParams = {
            ...searchParamsStructure,
            all: true,
            scope: controlEntitiesEnabled ? "all" : "risk",
        };

        const focusedEntity = this.focusedEntity();

        const updateRootNodeLinkedResources = <
            K extends keyof RiskDirectoryNodeResources,
        >(
            callback: (
                prevState: RiskDirectoryNodeResources
            ) => Pick<RiskDirectoryNodeResources, K>
        ) => {
            if (this.asyncComponent.current === undefined) {
                return;
            }
            const data = this.asyncComponent.current?.getData();
            if (data === undefined) {
                return;
            }
            const rootNode = data[1];
            const newLinkedResources = callback(rootNode.linkedResources);
            this.asyncComponent.current?.updateData2(
                set(rootNode, {
                    linkedResources: set(
                        rootNode.linkedResources,
                        newLinkedResources
                    ),
                })
            );
        };

        return (
            <AsyncComponent4<
                R.RiskDirectoryView,
                T.RiskDirectoryNodeDtoWithChanges,
                EntityOverviewListDto,
                ControlOverviewListDto
            >
                key={`risk-directory-${id}-${focus}-${at}`}
                ref={this.asyncComponent}
                ajaxManager={ajaxManager}
                promises={[
                    () =>
                        this.cachedPromiseRiskDirView.get(
                            searchParamsStructure,
                            () =>
                                getRisksRiskDirectoryId(
                                    Number(id),
                                    at !== "live" ? { at } : undefined
                                ).then((response) => ({
                                    ...response,
                                    data: response.data,
                                }))
                        ),
                    () =>
                        this.cachedPromiseRiskDirNode.get(
                            searchParamsNodes,
                            () =>
                                getRisksRiskDirectoryIdNodes(
                                    Number(id),
                                    searchParamsNodes
                                )
                        ),
                    () =>
                        this.cachedPromiseEntities.get(
                            searchParamsEntities,
                            () => getEntities(searchParamsEntities)
                        ),
                    // Risk of desync:
                    // Whenever you update that query, you need to also update the query in
                    // `updateAllControls` method below.
                    () => getAllControls(),
                ]}
                renderLoading={() => <PageSpinner />}
                renderError={(code) => <LoggedInHTTPError code={code} />}
                renderContent={(
                    riskDirectoryView,
                    rootNode,
                    entityList,
                    allControls
                ) => {
                    return (
                        <Directory
                            deleteDirectory={this.delete}
                            updateNode={this.updateNode}
                            reloadDirectory={this.reloadDirectory}
                            onDeleteNode={this.handleRootNodeDelete}
                            createNode={this.createNode}
                            mode={mode}
                            ajaxManager={ajaxManager}
                            orgCookie={orgCookie}
                            orgProfileCookie={orgProfileCookie}
                            riskDirectoryView={riskDirectoryView}
                            rootNode={rootNode}
                            entityList={entityList}
                            allControls={allControls}
                            lastAddedNodeId={lastAddedNodeId}
                            focusedEntity={focusedEntity}
                            updateRootNodeLinkedResources={
                                updateRootNodeLinkedResources
                            }
                        />
                    );
                }}
            />
        );
    };

    cancelAllUpdate() {
        this.cancelTokenSource.forEach((cts) => cts.cancel());
        this.cancelTokenSource = [];
    }

    delete = () => {
        const [riskDirectoryView] = this.asyncComponent.current!.getData()!;
        const { ajaxManager, navigate } = this.props;

        ajaxManager
            .ajax<void, DirectoryLoaderStatus>(
                () =>
                    deleteRisksRiskDirectoryId(
                        riskDirectoryView.content.resource.id
                    ),
                {
                    component: this,
                    initialStatusValue: "DEFAULT",
                    getErrorStatus: () => ({ type: "DEFAULT" }),
                    inFlightStatus: { type: "DELETING" },
                    getSuccessStatus: () => ({ type: "DEFAULT" }),
                }
            )
            .then(() => navigate(RISK_MODULE));
    };

    reloadDirectory = () => {
        this.asyncComponent.current?.reload();
    };

    updateNode: <NODE extends T.AnyNode>(
        action: T.NodeUpdateAction<NODE>,
        targetNode: NODE
    ) => Promise<void> = (action, targetNode): Promise<void> => {
        const [riskDirectoryView] = this.asyncComponent.current!.getData()!;
        const { ajaxManager } = this.props;
        const update = action(targetNode);
        if (!update) {
            return Promise.resolve(undefined);
        }

        const source = axios.CancelToken.source();
        this.cancelTokenSource.push(source);

        const updatedNodeId = getNodeGuid(targetNode);

        return ajaxManager
            .ajax<RN.SingleRiskDirectoryNodeView, DirectoryLoaderStatus>(
                () =>
                    patchRisksRiskDirectoryIdNodesNodeId(
                        riskDirectoryView.content.resource.id,
                        updatedNodeId,
                        update,
                        { cancelToken: source.token }
                    ),
                {
                    component: this,
                    initialStatusValue: "DEFAULT",
                    inFlightStatus: { type: "UPDATING" },
                    getSuccessStatus: () => ({ type: "DEFAULT" }),
                    getErrorStatus: () => ({ type: "DEFAULT" }),
                }
            )
            .then(async (updatedNode: RN.SingleRiskDirectoryNodeView) => {
                // If this is not a leaf node, we need to reload the whole tree.
                // This is because changing node can affect descendants.
                // For example, if we unassign an entity from the process or directory,
                //  not only the node will change, but also all risks that have metadata values.
                switch (updatedNode.type) {
                    case "RiskDirectory":
                    case "Process":
                        return this.asyncComponent.current!.reload();
                    case "Risk": {
                        // Extended risk nodes may not affect risk map availability, no reload required
                        if (targetNode.type === "ExpandedRiskNode") {
                            break;
                        }

                        // Global risk may change risk map availability, therefore require a reload if entities have changed
                        if (
                            updatedNode.content.scope.type !==
                            "GlobalWithContent"
                        ) {
                            break;
                        }

                        const targetEntityIds = (
                            (targetNode as typeof updatedNode).content
                                .scope as typeof updatedNode.content.scope
                        ).entityIds;

                        if (
                            isEqual(
                                targetEntityIds,
                                updatedNode.content.scope.entityIds
                            )
                        ) {
                            break;
                        }

                        return this.asyncComponent.current!.reload();
                    }
                    default:
                        assertNever(updatedNode);
                }

                // Otherwise we want to update the node in the tree, instead of reloading it, because:
                // * It's faster
                // * Our data view indexes are invalidated and these depend on `lastChanges` field

                const [
                    _a,
                    {
                        content: { resource, actions },
                        linkedResources: resources,
                    },
                    _c,
                    _allControls,
                ] = this.asyncComponent.current!.getData()!;
                const newRootNode = updateDirectorySubTree(
                    resource.rootNode,
                    (node) => {
                        if (node.id !== updatedNode.id) {
                            return node;
                        }
                        if (node.type !== updatedNode.type) {
                            throw new Error(
                                "Node update must not change the node type from " +
                                    node.type +
                                    " to " +
                                    updatedNode.type
                            );
                        }
                        return updatedNode;
                    },
                    /* Stop on first update */ true
                );
                if (newRootNode === resource.rootNode) {
                    throw new Error(
                        "Did not find node to update " + updatedNode.id
                    );
                }

                const linkedResources = this.updateResources(
                    updatedNode,
                    resources
                );
                await this.updateAllControls(linkedResources);

                const newRootNodeDto: T.RiskDirectoryNodeDtoWithChanges = {
                    content: {
                        resource: {
                            rootNode: newRootNode as R.RiskDirectoryNode,
                            riskMapValidity: resource.riskMapValidity,
                        },
                        actions,
                    },
                    linkedResources,
                    lastChanges: {
                        updated: [updatedNode],
                    },
                };

                return this.handleRootNodeUpdate(newRootNodeDto);
            });
    };

    // We have to update all controls whenever linked resources contain new controls
    updateAllControls = async (resources: RiskDirectoryNodeResources) => {
        const { ajaxManager } = this.props;
        const currentAllControls: ControlOverviewListDto =
            this.asyncComponent.current!.getData()![3];

        const isAnyControlMissing = !resources.controls.every((control) =>
            currentAllControls.content.some((c) => c.resource.id === control.id)
        );

        if (isAnyControlMissing) {
            // Risk of desync:
            // Whenever you update that query, you need to also update the original query in
            // AsyncComponent promises attribute.
            //
            // Temporary solution:
            // This is a temporary solution. Related resources lack control actions,
            // so we need to fetch all controls again.
            const allControls = await ajaxManager.ajax<
                ControlOverviewListDto,
                DirectoryLoaderStatus
            >(() => getAllControls(), {
                component: this,
                initialStatusValue: "UPDATING",
                inFlightStatus: { type: "UPDATING" },
                getSuccessStatus: () => ({ type: "DEFAULT" }),
                getErrorStatus: () => ({ type: "DEFAULT" }),
            });

            await this.asyncComponent.current!.updateData4(allControls);
        }
    };

    updateResources = (
        updatedNode: RN.SingleRiskDirectoryNodeView,
        resources: RiskDirectoryNodeResources
    ): RiskDirectoryNodeResources => {
        // We only add missing controls & control programs, removal would require a full tree traversal and feel unnecessary
        const updatedResources: RiskDirectoryNodeResources = {
            controls: resources.controls.slice(),
            controlPrograms: resources.controlPrograms.slice(),
            entities: resources.entities.slice(),
            users: resources.users.slice(),
            blobs: resources.blobs.slice(),
        };

        switch (updatedNode.type) {
            case "Risk": {
                return this.updateRiskResources(updatedNode, updatedResources);
            }
            case "RiskDirectory":
            case "Process":
                return updatedResources;
            default:
                assertNever(updatedNode);
        }
    };

    updateRiskResources = (
        updatedNode: R.SingleRiskNodeView,
        updatedResources: RiskDirectoryNodeResources
    ): RiskDirectoryNodeResources => {
        const nodeRelatedResources = updatedNode.resources;

        function addControlResource(controlId: Guid) {
            if (
                updatedResources.controls.findIndex(
                    (control) => control.id === controlId
                ) < 0
            ) {
                const control = nodeRelatedResources.controls.find(
                    (control) => control.id === controlId
                );
                if (control === undefined) {
                    throw new Error(`Cannot find control id ${controlId}`);
                }
                updatedResources.controls.push(control);

                if (
                    updatedResources.controlPrograms.findIndex(
                        (controlProgram) =>
                            controlProgram.resource.id ===
                            control.controlProgramId
                    ) < 0
                ) {
                    const controlProgram =
                        nodeRelatedResources.controlPrograms.find(
                            (cp) => cp.resource.id === control.controlProgramId
                        );
                    if (controlProgram === undefined) {
                        throw new Error(
                            `Cannot find controlProgram id ${control.controlProgramId}`
                        );
                    }
                    updatedResources.controlPrograms.push(controlProgram);
                }
            }
        }

        const scope = updatedNode.content.scope;
        switch (scope.type) {
            case "LocalWithContent":
                scope.content.mitigatingControls.forEach((mitigatingControl) =>
                    addControlResource(mitigatingControl.controlId)
                );
                break;
            case "GlobalWithContent":
                Object.values(scope.entityIdContent).forEach((content) =>
                    content.mitigatingControls.forEach((mitigatingControl) =>
                        addControlResource(mitigatingControl.controlId)
                    )
                );
                break;
            default:
                assertNever(scope);
        }

        return updatedResources;
    };

    handleRootNodeUpdate = (
        rootNode: T.RiskDirectoryNodeDtoWithChanges
    ): Promise<void> => {
        return this.asyncComponent.current!.updateData2(rootNode);
    };

    handleRootNodeDelete = (node: T.DirectoryNode): void => {
        const [_a, rootNode] = this.asyncComponent.current!.getData()!;

        // Cancel all Axios UPDATE queries
        this.cancelAllUpdate();

        // The Root Node contains the actions, we need to delete the IDs of
        // the nodes that we will remove from the actions to avoid any kind of problem
        // when creating new nodes afterwards.
        const idSet = new Set<string>();
        getSubsequentIds(node, idSet);

        // Computes the new rootNode and set actions accordingly
        const newNode = updateDirectorySubTree(
            rootNode.content.resource.rootNode,
            (n) => (n.id === node.id ? undefined : n),
            true
        ) as typeof rootNode.content.resource.rootNode;

        this.asyncComponent.current!.updateData2({
            content: {
                resource: {
                    rootNode: newNode,
                    riskMapValidity: [],
                },
                actions: {
                    delete: rootNode.content.actions.delete.filter(
                        (id) => !idSet.has(id)
                    ),
                    editEntityFields:
                        rootNode.content.actions.editEntityFields.filter(
                            (id) => !idSet.has(id)
                        ),
                    editRiskDirectoryFields:
                        rootNode.content.actions.editRiskDirectoryFields.filter(
                            (id) => !idSet.has(id)
                        ),
                },
            },
            linkedResources: rootNode.linkedResources,
            lastChanges: {
                deleted: idSet,
            },
        });
    };

    createNode = (newNode: R.NewRiskDirectoryNode): Promise<void> => {
        const { parentNodeId } = newNode;
        const [riskDirectoryView] = this.asyncComponent.current!.getData()!;
        const { ajaxManager } = this.props;

        return ajaxManager
            .ajax<RN.SingleRiskDirectoryNodeView, DirectoryLoaderStatus>(
                () =>
                    postRisksRiskDirectoryIdNodes(
                        riskDirectoryView.content.resource.id,
                        newNode
                    ),
                {
                    component: this,
                    initialStatusValue: "DEFAULT",
                    getErrorStatus: () => {
                        return { type: "DEFAULT" }; // Do not change the status on error, it's only a local error.
                    },
                    inFlightStatus: { type: "UPDATING" },
                    getSuccessStatus: ([response]) => {
                        let newNode: R.StandardNode;
                        switch (response.type) {
                            case "Risk":
                            case "Process":
                                newNode = newViewNodeToStandardNode(response);
                                break;
                            case "RiskDirectory":
                                throw new Error(
                                    "Unexpected object: " +
                                        JSON.stringify(response)
                                );
                            default:
                                assertNever(response);
                        }

                        const [
                            _a,
                            {
                                content: { resource, actions },
                                linkedResources: resources,
                            },
                            _c,
                            _allControls,
                        ] = this.asyncComponent.current!.getData()!;
                        const newRootNode = updateDirectorySubTree(
                            resource.rootNode,
                            (parentNode) => {
                                if (parentNode.id !== parentNodeId) {
                                    return parentNode;
                                }
                                // Reinstate children if need be
                                switch (parentNode.type) {
                                    case "RiskDirectory":
                                    case "Process": {
                                        const { nodes, ...rest } = parentNode;
                                        return {
                                            ...rest,
                                            nodes: [...nodes, newNode],
                                        };
                                    }
                                    case "Risk": {
                                        throw new Error(
                                            `Cannot append a node to risk node ${parentNodeId}`
                                        );
                                    }
                                    default: {
                                        return assertNever(parentNode);
                                    }
                                }
                            },
                            /* Stop on first update */ true
                        );
                        if (newRootNode === resource.rootNode) {
                            throw new Error(
                                `Did not find parent node ${parentNodeId}`
                            );
                        }

                        const linkedResources = this.updateResources(
                            response,
                            resources
                        );
                        this.updateAllControls(linkedResources);

                        const newRootNodeDto: T.RiskDirectoryNodeDtoWithChanges =
                            {
                                content: {
                                    resource: {
                                        rootNode:
                                            newRootNode as R.RiskDirectoryNode,
                                        riskMapValidity:
                                            resource.riskMapValidity,
                                    },
                                    actions: {
                                        delete: [...actions.delete, newNode.id],
                                        editEntityFields: [
                                            ...actions.editEntityFields,
                                            newNode.id,
                                        ],
                                        editRiskDirectoryFields: [
                                            ...actions.editRiskDirectoryFields,
                                            newNode.id,
                                        ],
                                    },
                                },
                                linkedResources,
                                lastChanges: {
                                    inserted: [newNode],
                                },
                            };

                        this.asyncComponent.current!.updateData2(
                            newRootNodeDto
                        );
                        this.setState({
                            lastAddedNodeId: newNode?.id,
                        });
                        return { type: "DEFAULT" };
                    },
                }
            )
            .then(() => {});
    };
}

function newViewNodeToStandardNode(
    newNode: R.SingleStandardNodeView
): R.StandardNode {
    switch (newNode.type) {
        case "Risk":
            return newNode;
        case "Process":
            return { ...newNode, nodes: [] };
        default:
            return assertNever(newNode);
    }
}

interface DirectoryLoaderProps extends WithRouterProps {
    mode: DirectoryMode;
    ajaxManager: AjaxManager;
    orgCookie: OrgCookie;
    orgProfileCookie: UserProfile | null;
    controlEntitiesEnabled: boolean;
    params: Params;
}

type DirectoryLoaderStatus =
    | {
          type: "DEFAULT";
      }
    | {
          type: "UPDATING";
      }
    | {
          type: "DELETING";
      };

interface DirectoryLoaderState {
    status: DirectoryLoaderStatus;
    lastAddedNodeId: Guid | undefined;
}

const DirectoryLoaderWithRouter = withRouter(withParams(DirectoryLoader));

interface RiskDirectoryViewProps {
    ajaxManager: AjaxManager;
    orgCookie: OrgCookie;
    orgProfileCookie: UserProfile | null;
}

export function RiskDirectoryView(props: RiskDirectoryViewProps) {
    const getUrlDate = memoizeOne((at: string | null): Moment | null => {
        return at ? moment(at) : null;
    });
    const { ajaxManager, orgCookie, orgProfileCookie } = props;
    const location = useLocation();

    const controlEntitiesEnabled = useFeatureFlag("controlEntities");

    const params = new URLSearchParams(location.search);
    const at = params.get(RISK_DIRECTORY_DATE_SEARCH_PARAM);
    const from = params.get(RISK_DIRECTORY_FROM_DATE_SEARCH_PARAM);
    const to = params.get(RISK_DIRECTORY_TO_DATE_SEARCH_PARAM);
    const urlAtDate = getUrlDate(at);
    const urlFromDate = getUrlDate(from);
    const urlToDate = getUrlDate(to);
    const filterParam = params.get(RISK_DIRECTORY_FILTER_SEARCH_PARAM);
    const filter =
        filterParam !== null
            ? (JSON.parse(filterParam) as SimpleAndFilter)
            : null;

    return (
        <HashSwitch
            hashRoutes={{
                "": (
                    <DirectoryLoaderWithRouter
                        mode={{
                            type: "Map",
                            savedDate: urlAtDate,
                            filter,
                        }}
                        ajaxManager={ajaxManager}
                        orgCookie={orgCookie}
                        orgProfileCookie={orgProfileCookie}
                        controlEntitiesEnabled={controlEntitiesEnabled}
                    />
                ),
                [RISK_DIRECTORY_HASH]: (
                    <DirectoryLoaderWithRouter
                        mode={{
                            type: "Directory",
                            savedDate: urlAtDate,
                        }}
                        ajaxManager={ajaxManager}
                        orgCookie={orgCookie}
                        orgProfileCookie={orgProfileCookie}
                        controlEntitiesEnabled={controlEntitiesEnabled}
                    />
                ),
                [RISK_MAP_HASH]: (
                    <DirectoryLoaderWithRouter
                        mode={{
                            type: "Map",
                            savedDate: urlAtDate,
                            filter,
                        }}
                        ajaxManager={ajaxManager}
                        orgCookie={orgCookie}
                        orgProfileCookie={orgProfileCookie}
                        controlEntitiesEnabled={controlEntitiesEnabled}
                    />
                ),
                [RISK_MAP_HISTORY_HASH]: (
                    <DirectoryLoaderWithRouter
                        mode={{
                            type: "HistoricalMap",
                            targetDate: urlAtDate,
                            filter,
                        }}
                        ajaxManager={ajaxManager}
                        orgCookie={orgCookie}
                        orgProfileCookie={orgProfileCookie}
                        controlEntitiesEnabled={controlEntitiesEnabled}
                    />
                ),
                [RISK_MAP_INSIGHTS_HASH_OLD]: (
                    <DirectoryLoaderWithRouter
                        mode={{
                            type: "InsightsMap",
                            fromDate: urlFromDate,
                            toDate: urlToDate,
                            filter,
                        }}
                        ajaxManager={ajaxManager}
                        orgCookie={orgCookie}
                        orgProfileCookie={orgProfileCookie}
                        controlEntitiesEnabled={controlEntitiesEnabled}
                    />
                ),
                [RISK_MAP_INSIGHTS_HASH]: (
                    <Suspense fallback={<Spin />}>
                        <MapInsights start={urlFromDate!} end={urlToDate!} />
                    </Suspense>
                ),
            }}
        />
    );
}
